/**
 * The order page's edits (TCG Fulfill Order): the address's country choices, the notes' links,
 * the total cost typed in dollars and when a tracking edit asks first. Pure, so tested alone.
 */

/** Retool's Note Links: every http(s) URL in the notes, in the order written, each once. */
export function noteLinks(notes: string | null): string[] {
  const found = notes?.match(/https?:\/\/[^\s<>"')\]]+/g) ?? [];
  return [...new Set(found.map((url) => url.replace(/[.,;:!?]+$/, "")))];
}

/** A typed total cost: cents, null for a blank (clears it), or why it is refused. */
export type TotalCostInput = { cents: number | null } | { error: string };

/**
 * The total cost as typed in dollars ("12.5", "$1,200"): rounded to whole cents as Retool did. A
 * blank clears the cost and 0 is $0.00; Retool sent 0 as no value, which cleared it too.
 */
export function parseTotalCostInput(text: string): TotalCostInput {
  const cleaned = text.trim().replace(/^\$/, "").replaceAll(",", "");
  if (cleaned === "") return { cents: null };
  if (!/^\d+(\.\d*)?$|^\.\d+$/.test(cleaned)) return { error: "Enter a dollar amount." };
  const cents = Math.round(Number(cleaned) * 100);
  if (!Number.isSafeInteger(cents) || cents > 2_147_483_647) {
    return { error: "That amount is too large." };
  }
  return { cents };
}

/**
 * Whether saving `next` replaces or clears a tracking number the order already has, which the
 * page asks about first. Retool saved over it the moment the field lost focus.
 */
export function replacesTracking(current: string | null, next: string): boolean {
  return Boolean(current?.trim()) && next.trim() !== current?.trim();
}

/**
 * Edit Address's country choices: the countries the order's environment ships to, with the
 * order's own country first when it is not among them, since a same-country repair always saves.
 */
export function addressCountryChoices(
  shippingCountries: readonly string[],
  orderCountry: string | null,
): string[] {
  if (!orderCountry || shippingCountries.includes(orderCountry)) return [...shippingCountries];
  return [orderCountry, ...shippingCountries];
}
